import { CurrencyCode } from '@percent/utility'

// Frontend mirror of the generic insights platform contract served by pbp-api at:
//   GET /v1/insights/<domain>/metrics/<slug>?<filters>  → { object: 'insights_metric',  data }
//   GET /v1/insights/<domain>/filters                   → { object: 'insights_filters', data }
//
// Teams own the data + metadata (titles, labels, series taxonomy) via their backend
// metric/filter definitions; this library owns the generic rendering. Keep these
// types in lock-step with `partner-insights/insights/insights.types.ts` on the API.

// ─────────────────────────────────────────────────────────────────────────────
// Values
// ─────────────────────────────────────────────────────────────────────────────

/** A monetary value on the wire: integer amount in **minor units** + ISO 4217 code. */
export type MoneyValue = {
  type: 'money'
  amount: number
  currency: CurrencyCode
}

/** The units a {@link DurationValue} can be expressed in. */
export type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days'

/** A span of time on the wire: a magnitude in the stated {@link DurationUnit}. */
export type DurationValue = {
  type: 'duration'
  value: number
  unit: DurationUnit
}

/**
 * A single metric value. A bare `number` is a plain scalar (count, percent) and the
 * untagged case; {@link MoneyValue} and {@link DurationValue} are tagged by `type`.
 * So test `typeof value === 'number'` first, then discriminate on `type`.
 */
export type ValueType = number | MoneyValue | DurationValue

// Takes `unknown` rather than `ValueType` so the same guard narrows a wider union —
// a `DatasetCellValue` cell, which may also hold a string or a tag list.
const isTagged = <T extends MoneyValue | DurationValue>(
  value: unknown,
  type: T['type']
): value is T =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === type

export const isMoneyValue = (value: unknown): value is MoneyValue =>
  isTagged<MoneyValue>(value, 'money')

export const isDurationValue = (value: unknown): value is DurationValue =>
  isTagged<DurationValue>(value, 'duration')

/**
 * Pulls the numeric magnitude out of any {@link ValueType} — minor units for money,
 * the count of {@link DurationValue.unit}s for a duration — so chart records and
 * axis scales carry plain numbers.
 */
export const valueAmount = (value: ValueType): number => {
  if (typeof value === 'number') return value

  return isMoneyValue(value) ? value.amount : value.value
}

/**
 * A stable token for a value's unit: two values sharing a token can share one chart
 * axis and one formatter, so a count, a money amount, and an hours duration each get
 * their own scale (as do minutes vs hours, whose magnitudes aren't comparable).
 * An absent sample groups with plain numbers — the neutral, unformatted scale.
 */
export const valueUnitKey = (value: ValueType | undefined): string => {
  if (value === undefined || typeof value === 'number') return 'number'

  return isMoneyValue(value)
    ? `money:${value.currency}`
    : `duration:${value.unit}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Metric data shapes
// ─────────────────────────────────────────────────────────────────────────────

/** Rendering hint: a line chart (`timeseries`), a plain table (`rows`), or shares of a whole. */
export type RowStructure = 'timeseries' | 'rows' | 'percentageRows'

/**
 * Structured period-over-period trend (pbp-api#12481): the prior-period baseline
 * `value`, the `difference` (current − prior), the fractional `percentage` change
 * (0–1 scale, e.g. 0.25 = +25%), and the comparison `period`. `percentage` is
 * omitted when the baseline is non-positive (pbp-api#13174): 0 divides by zero,
 * negative makes a rise read as a fall, so the prior value + difference are still
 * returned without a misleading percentage; derive direction from `difference`.
 */
export type TrendData = {
  value: ValueType
  difference: ValueType
  percentage?: number
  period: { from: string; to: string }
}

/** A single headline value, optionally with a {@link TrendData} period-over-period trend. */
export type ScalarData = {
  type: 'scalar'
  value: ValueType
  /** Period-over-period trend; absent when there's no comparison window (e.g. all-time). */
  trend?: TrendData
}

/** One row of a flat (non-series-split) breakdown. */
export type RowItem = {
  key: string
  label: string
  description?: string
  value: ValueType
  /** share of the total, for `percentageRows`. */
  percent?: number
  /** catch-all bucket ("Other"/"Unknown"); the platform pins these to the end. */
  catchAll?: boolean
}

/**
 * Names the dimension a row *is* — the row-side counterpart of a
 * {@link SeriesDefinition}: `series` names the columns, `rowAxis` names the key
 * column (e.g. `{ key: 'employee_country', label: 'Country' }`). `label` arrives
 * already localised. `key` is the grouping field's machine id — for keying and
 * analytics only, never rendered, exactly like a row's own `key`.
 *
 * Absent when no single name fits: a composite (multi-dimension) grouping, an
 * ungrouped single-row result, or a producer that predates the field. Never
 * present on a {@link ScalarData}.
 */
export type RowAxis = {
  key: string
  label: string
  description?: string
}

/** A flat breakdown (no series split). */
export type RowsData = {
  type: 'rows'
  structure: RowStructure
  /** What one row is (the key column's heading / category axis title). */
  rowAxis?: RowAxis
  rows: RowItem[]
}

/** Declares one series (column) of a {@link SeriesRowsData} breakdown. */
export type SeriesDefinition = {
  key: string
  label: string
  description?: string
  catchAll?: boolean
}

/** One series' value within a {@link SeriesRowItem}. */
export type SeriesRowValue = {
  /** matches a {@link SeriesDefinition.key}. */
  series: string
  value: ValueType
}

/** One row of a series-split breakdown: a row total plus a value per series. */
export type SeriesRowItem = {
  key: string
  label: string
  description?: string
  total: ValueType
  percent?: number
  values: SeriesRowValue[]
  catchAll?: boolean
}

/** A breakdown split into series (e.g. rows = cause areas, series = donation categories). */
export type SeriesRowsData = {
  type: 'seriesRows'
  structure: RowStructure
  /** What one row is (the key column's heading / category axis title). */
  rowAxis?: RowAxis
  series: SeriesDefinition[]
  rows: SeriesRowItem[]
}

/** The discriminated payload a metric returns; the platform wraps it in the envelope. */
export type MetricData = ScalarData | RowsData | SeriesRowsData

/** The `data` of the `{ object: 'insights_metric', data }` response. */
export type MetricResponsePayload = {
  metric: string
  title: string
  subtitle?: string
  description?: string
  data: MetricData
}

// ─────────────────────────────────────────────────────────────────────────────
// Filters
// ─────────────────────────────────────────────────────────────────────────────

// `isoDatetime` (dateFrom/dateTo) is legacy — the backend will remove it once the
// `dateRange` filter has fully replaced it; the FE already uses `dateRange`.
export type FilterValueType =
  | 'stringArray'
  | 'enum'
  | 'enumArray'
  | 'isoDatetime'
  | 'dateRange'
  | 'numberRange'

/** A relative date-range preset offered by a `dateRange` filter (e.g. a partner's fiscal year). */
export type DateRangePreset = { key: string; label: string }

/**
 * A selectable filter option. A bare string is its own value + label (e.g. activity
 * tags / regions, which arrive as display names); a `{ value, label }` carries a
 * separate display label for values that aren't display-ready (e.g. donation
 * category enum values, whose labels the backend resolves via i18n).
 */
export type FilterOption = string | { value: string; label: string }

/** Normalises a {@link FilterOption} to `{ value, label }` for rendering. */
export const filterOptionValueLabel = (
  option: FilterOption
): { value: string; label: string } =>
  typeof option === 'string'
    ? { value: option, label: option }
    : { value: option.value, label: option.label }

/**
 * The value of a `dateRange` filter (mutually exclusive): a relative preset token,
 * a custom `{ from, to }` range, or neither (all-time). Serialised to the
 * `period` / `from` / `to` query params the backend resolves.
 */
export type DateRangeValue =
  | { period: string }
  | { from: string; to: string }
  | undefined

/** A filter as returned by `GET /v1/insights/<domain>/filters` (definition + resolved options). */
export type ResolvedFilter = {
  key: string
  label: string
  description?: string
  valueType: FilterValueType
  /** selectable options for `static` / `values` sources (bare string or `{ key, label }`). */
  values?: FilterOption[]
  /** partner-resolved relative presets for a `dateRange` filter. */
  presets?: DateRangePreset[]
  /** data bounds for the custom picker (`dateRange`) or an `isoDatetime` filter. */
  range?: { min: string | null; max: string | null }
  /** data bounds for a `numberRange` filter, in major units when `currency` is set. */
  bounds?: { min: number | null; max: number | null }
  /** ISO 4217 code when a `numberRange` filter's values are money. */
  currency?: CurrencyCode
  /**
   * `values` filters only: `true` when more options exist beyond the inline first
   * page, so the client searches/paginates via {@link InsightsFilterValuesEnvelope}.
   * Absent/`false` means the inline `values` list is complete (search client-side).
   */
  hasMoreValues?: boolean
  /**
   * A dependent (cascading) picker: the filter keys whose current selection narrows
   * this filter's options — e.g. `taskTemplateNames` dependsOn `programNames`, so only
   * the selected programmes' templates are on offer. Send those selections as query
   * params to the values endpoint and refetch this picker whenever one changes. The
   * inline `values` above are always **unscoped** (`/filters` is the page-load call,
   * before anything is selected), so a dependent picker reads its options from the
   * endpoint as soon as a dependency is selected.
   */
  dependsOn?: string[]
}

/**
 * Which edge a filter's dropdown lines up with: `'end'` hangs it off the
 * trigger's right edge (for a right-aligned bar, e.g. the dashboard header),
 * `'start'` off the left edge (for a left-aligned bar, e.g. a report page).
 */
export type FilterDropdownAlign = 'start' | 'end'

/**
 * The wire/value keys a filter is backed by: most filters map 1:1 to their own
 * `key`; a `dateRange` is backed by the `period` / `from` / `to` params instead,
 * and a `numberRange` by `<key>Min` / `<key>Max`.
 */
export const filterFieldKeys = (def: ResolvedFilter): string[] => {
  if (def.valueType === 'dateRange') return ['period', 'from', 'to']
  if (def.valueType === 'numberRange') return [`${def.key}Min`, `${def.key}Max`]

  return [def.key]
}

/**
 * Active filter values keyed by filter key. A single string for `enum`/`isoDatetime`,
 * a string array for `stringArray`/`enumArray`. `undefined` means "not applied".
 */
export type FilterValues = Record<string, string | string[] | undefined>

// ─────────────────────────────────────────────────────────────────────────────
// Response envelopes
// ─────────────────────────────────────────────────────────────────────────────

export type InsightsMetricEnvelope = {
  object: 'insights_metric'
  data: MetricResponsePayload
}

export type InsightsFiltersEnvelope = {
  object: 'insights_filters'
  data: ResolvedFilter[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter values (searchable, keyset-paginated)
// ─────────────────────────────────────────────────────────────────────────────
//
// Frontend mirror of the searchable, keyset-paginated filter-values endpoint
// (pbp-api, NEX-746):
//   GET /v1/insights/<domain>/filters/<filter>/values?search&cursor&pageSize
//     → { object: 'insights_filter_values', data, pageSize, _links }
// The value is its own keyset cursor, so there's no `totalResults` — `_links.next`
// drives "load more". Only dynamic `values` filters are served; others 404.

/** One selectable filter value; `value` is submitted, `label` is displayed. */
export type FilterValueOption = { value: string; label: string }

/** Keyset-pagination links (absolute URLs carrying the `cursor` query param). */
export type FilterValuesLinks = {
  current?: string
  next?: string
  prev?: string
}

/** The `{ object: 'insights_filter_values', ... }` paginated response. */
export type InsightsFilterValuesEnvelope = {
  object: 'insights_filter_values'
  data: FilterValueOption[]
  pageSize: number
  _links: FilterValuesLinks
}

// ─────────────────────────────────────────────────────────────────────────────
// Datasets (paginated tabular rows)
// ─────────────────────────────────────────────────────────────────────────────
//
// Frontend mirror of the self-describing dataset response served by pbp-api at:
//   GET /v1/insights/<domain>/datasets/<dataset>?<filters>&sort&direction&cursor&pageSize&columns
//     → { object: 'insights_dataset', columns, data: rows, totalResults, pageSize, _links }
// Keyset (cursor) pagination: `_links.next` / `_links.prev` carry the cursor.

/** How a dataset column's cells are rendered. */
export type DatasetColumnDataType =
  | 'string'
  | 'money'
  | 'duration'
  | 'datetime'
  | 'stringArray'

/** A column's fixed value→label set (e.g. donation types), resolved server-side for the locale. */
export type DatasetColumnValue = { value: string; label: string }

/** A dataset column's metadata (the response is self-describing). */
export type DatasetColumn = {
  key: string
  label: string
  dataType: DatasetColumnDataType
  sortable?: boolean
  values?: DatasetColumnValue[]
  /** The column group it was added with via `columnGroups` (e.g. one column per form question of the selected grant programme). */
  group?: string
}

/**
 * Opts a dataset table or export into one of its column groups (API
 * `columnGroups`), e.g. all form submissions of the selected grant programme.
 * The API resolves a group only for some filter selections, so `isAvailable`
 * gates the request and `unavailable` explains why.
 */
export type DynamicColumnsOption = {
  group: string
  label: string
  unavailable: string
  isAvailable: (filters: FilterValues) => boolean
  /**
   * Lets the export pick parts of the group instead of all of it, e.g. form
   * templates and their questions. Asked for as `<group>:<part>` and
   * `<group>:<part>:<item>`.
   */
  parts?: {
    label: string
    selectAllLabel: string
    load: (filters: FilterValues) => Promise<DynamicColumnsPart[]>
  }
}

export type DynamicColumnsPart = {
  id: string
  label: string
  items: { id: string; label: string }[]
}

/**
 * A single cell value: text/datetime (string), a tag list (stringArray), or a
 * {@link ValueType} magnitude — the same value shape a metric carries, so a money
 * or duration cell arrives tagged exactly as it does on a metric.
 */
export type DatasetCellValue = string | string[] | ValueType | null

/** One dataset row keyed by column key; always carries an `id`. */
export type DatasetRow = { id: string } & Record<string, DatasetCellValue>

/** Cursor-pagination links (absolute URLs carrying the keyset `cursor` query param). */
export type DatasetLinks = { self?: string; next?: string; prev?: string }

/** The `{ object: 'insights_dataset', ... }` paginated response. */
export type InsightsDatasetEnvelope = {
  object: 'insights_dataset'
  dataset: string
  title?: string
  description?: string
  columns: DatasetColumn[]
  data: DatasetRow[]
  totalResults: number
  pageSize: number
  _links: DatasetLinks
}

// The CSV export endpoint (`…/datasets/:dataset/csv`, NEX-712 PR 2) streams a
// `text/csv` attachment — the response body IS the CSV text, so there's no JSON
// envelope type for it; `InsightsService.exportDataset` resolves to the raw string.
